import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { ScrollArea, ScrollAreaViewport, ScrollAreaScrollbar } from '@/components/ui/scroll-area';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Maximize2, Minimize2, AlertTriangle } from 'lucide-react';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

export type BudgetType = 'resource' | 'cost' | 'sales';

interface ShiftBudgetSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  roleName: string;
  monthlyHours: number[];
  selectedMonthIndex?: number; // The month cell that was selected when Shift was clicked
  onShift: (sourceFrom: number, sourceTo: number, destinationStart: number, clearSource: boolean) => void;
  totalMonths?: number; // Total number of months in the project
  monthLabels?: string[]; // Custom month labels with year (e.g., "May/24", "Jun/24")
  budgetType?: BudgetType; // Type of budget (resource, cost, or sales)
}

export function ShiftBudgetSheet({
  open,
  onOpenChange,
  roleName,
  monthlyHours,
  selectedMonthIndex,
  onShift,
  totalMonths = 12,
  monthLabels,
  budgetType = 'resource',
}: ShiftBudgetSheetProps) {
  // Helper to get month label - use custom labels if provided, otherwise fall back to MONTHS
  const getMonthLabel = (index: number) => {
    if (monthLabels && monthLabels[index]) {
      return monthLabels[index];
    }
    return MONTHS[index % 12];
  };

  // Helper functions for dynamic text based on budget type
  const getBudgetLabel = () => {
    switch (budgetType) {
      case 'resource': return 'Resource Budget';
      case 'cost': return 'Cost Budget';
      case 'sales': return 'Sales Budget';
      default: return 'Budget';
    }
  };

  const getValueLabel = () => {
    switch (budgetType) {
      case 'resource': return 'hours';
      case 'cost': return '';
      case 'sales': return '';
      default: return '';
    }
  };

  const formatValue = (value: number) => {
    if (budgetType === 'resource') {
      return `${value.toFixed(2)} hours`;
    }
    // For cost and sales, use currency formatting
    return `£${value.toFixed(2)}`;
  };

  const formatValueOnly = (value: number) => {
    if (budgetType === 'resource') {
      return value.toFixed(2);
    }
    // For cost and sales, use currency formatting
    return `£${value.toFixed(2)}`;
  };

  const [sourceFrom, setSourceFrom] = useState<number>(0);
  const [sourceTo, setSourceTo] = useState<number>(0);
  const [destinationStart, setDestinationStart] = useState<number>(1);
  const [clearSource, setClearSource] = useState<boolean>(true);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showTruncationConfirm, setShowTruncationConfirm] = useState(false);

  // Reset state when dialog opens with new selected month
  useEffect(() => {
    if (open) {
      const monthIndex = selectedMonthIndex ?? 0;
      setSourceFrom(monthIndex);
      setSourceTo(monthIndex);
      setDestinationStart(Math.min(monthIndex + 1, totalMonths - 1));
      setClearSource(true);
    }
  }, [open, selectedMonthIndex, totalMonths]);

  // When sourceFrom changes, ensure sourceTo is not before it
  useEffect(() => {
    if (sourceTo < sourceFrom) {
      setSourceTo(sourceFrom);
    }
  }, [sourceFrom, sourceTo]);

  // Calculate shift amounts and truncation info
  const shiftAmount = destinationStart - sourceFrom;
  const destinationEnd = sourceTo + shiftAmount;
  const willTruncate = destinationEnd > totalMonths - 1;
  
  // Calculate truncation details
  const truncationInfo = (() => {
    if (!willTruncate) return null;
    
    // Find the first source month that would exceed the project range
    const firstTruncatedSourceIndex = totalMonths - shiftAmount;
    const truncatedMonthCount = sourceTo - firstTruncatedSourceIndex + 1;
    const truncatedHours = monthlyHours
      .slice(firstTruncatedSourceIndex, sourceTo + 1)
      .reduce((sum, h) => sum + h, 0);
    
    // Get the range label for truncated months
    const firstTruncatedLabel = getMonthLabel(firstTruncatedSourceIndex);
    const lastTruncatedLabel = getMonthLabel(sourceTo);
    const rangeLabel = truncatedMonthCount === 1 
      ? firstTruncatedLabel 
      : `${firstTruncatedLabel} - ${lastTruncatedLabel}`;
    
    return {
      monthCount: truncatedMonthCount,
      hours: truncatedHours,
      rangeLabel,
      firstTruncatedSourceIndex,
    };
  })();

  const handleApply = () => {
    if (sourceFrom > sourceTo) {
      alert('Source "From" month must be before "To" month');
      return;
    }

    // If truncation will occur, show confirmation dialog
    if (willTruncate) {
      setShowTruncationConfirm(true);
      return;
    }

    onShift(sourceFrom, sourceTo, destinationStart, clearSource);
    onOpenChange(false);
  };

  const handleConfirmTruncation = () => {
    // Proceed with the shift - the handler will only shift what fits
    onShift(sourceFrom, sourceTo, destinationStart, clearSource);
    setShowTruncationConfirm(false);
    onOpenChange(false);
  };

  // Calculate preview
  const sourceTotal = monthlyHours
    .slice(sourceFrom, sourceTo + 1)
    .reduce((sum, h) => sum + h, 0);
  
  // Calculate hours that will actually be shifted (excluding truncated)
  const shiftableHours = willTruncate && truncationInfo
    ? monthlyHours.slice(sourceFrom, truncationInfo.firstTruncatedSourceIndex).reduce((sum, h) => sum + h, 0)
    : sourceTotal;

  // Calculate total hours in destination range (after the shift)
  const actualDestEnd = Math.min(destinationEnd, totalMonths - 1);
  const destinationTotal = shiftableHours; // The hours that will be in the destination range

  // Calculate preview data showing Before/After for all affected months
  const previewData = (() => {
    // Get all unique month indices that are affected (source + destination range)
    const affectedMonths = new Set<number>();
    
    // Add source months
    for (let i = sourceFrom; i <= sourceTo; i++) {
      affectedMonths.add(i);
    }
    
    // Add destination months (only those within project range)
    const actualDestEnd = Math.min(destinationEnd, totalMonths - 1);
    for (let i = destinationStart; i <= actualDestEnd; i++) {
      affectedMonths.add(i);
    }
    
    // Sort the months
    const sortedMonths = Array.from(affectedMonths).sort((a, b) => a - b);
    
    // Calculate the "after" values for each month
    const afterValues: Record<number, number> = {};
    
    // Start with current values (copy them to afterValues)
    sortedMonths.forEach(monthIndex => {
      afterValues[monthIndex] = monthlyHours[monthIndex] || 0;
    });
    
    // Apply the shift logic
    // First, copy source values to destination
    for (let i = sourceFrom; i <= sourceTo; i++) {
      const destIndex = i + shiftAmount;
      if (destIndex >= 0 && destIndex < totalMonths) {
        afterValues[destIndex] = monthlyHours[i] || 0;
      }
    }
    
    // Then clear source if requested (only non-overlapping cells)
    if (clearSource) {
      const destEnd = sourceTo + shiftAmount;
      for (let i = sourceFrom; i <= sourceTo; i++) {
        if (i < destinationStart || i > destEnd) {
          afterValues[i] = 0;
        }
      }
    }
    
    // Build the preview cells with before and after values
    const cells = sortedMonths.map(monthIndex => ({
      monthIndex,
      label: getMonthLabel(monthIndex),
      before: monthlyHours[monthIndex] || 0,
      after: afterValues[monthIndex],
    }));
    
    return { cells };
  })();

  // Shared form content
  const FormContent = () => (
    <div className="space-y-6">
      {/* Source Range */}
      <div className="space-y-4">
        <h4 className="font-medium">Source Range</h4>
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-2">
            <Label>From Month</Label>
            <Select
              value={sourceFrom.toString()}
              onValueChange={(value) => setSourceFrom(parseInt(value))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Array.from({ length: totalMonths }, (_, index) => (
                  <SelectItem key={index} value={index.toString()}>
                    {getMonthLabel(index)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>To Month</Label>
            <Select
              value={sourceTo.toString()}
              onValueChange={(value) => setSourceTo(parseInt(value))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {/* Only show months >= sourceFrom */}
                {Array.from({ length: totalMonths }, (_, index) => index)
                  .filter(index => index >= sourceFrom)
                  .map((index) => (
                    <SelectItem key={index} value={index.toString()}>
                      {getMonthLabel(index)}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="text-sm text-muted-foreground">
          Total {getValueLabel() || 'value'} in source range: <span className="font-semibold">{formatValue(sourceTotal)}</span>
        </div>
      </div>

      {/* Destination */}
      <div className="space-y-4">
        <h4 className="font-medium">Destination</h4>
        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-2">
            <Label>Start Month</Label>
            <Select
              value={destinationStart.toString()}
              onValueChange={(value) => setDestinationStart(parseInt(value))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Array.from({ length: totalMonths }, (_, index) => (
                  <SelectItem key={index} value={index.toString()}>
                    {getMonthLabel(index)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>End Month</Label>
            <Select value={actualDestEnd.toString()} disabled>
              <SelectTrigger className="[&_svg]:hidden bg-muted/50 cursor-default">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={actualDestEnd.toString()}>
                  {getMonthLabel(actualDestEnd)}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="text-sm text-muted-foreground">
          Total {getValueLabel() || 'value'} in destination range: <span className="font-semibold">{formatValue(destinationTotal)}</span>
        </div>
      </div>

      {/* Options */}
      <div className="space-y-4">
        <h4 className="font-medium">Options</h4>
        <div className="flex items-center space-x-2">
          <Checkbox
            id="clearSource"
            checked={clearSource}
            onCheckedChange={(checked) => setClearSource(checked as boolean)}
          />
          <label
            htmlFor="clearSource"
            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          >
            Clear source months after shifting
          </label>
        </div>
      </div>

      {/* Preview */}
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <h4 className="font-medium">Preview</h4>
          {willTruncate && truncationInfo && (
            <AlertTriangle className="h-4 w-4 accent-warning text-[var(--accent-border)]" />
          )}
        </div>
        {/* Horizontal scrollable Before/After preview */}
        <div className="rounded-md border bg-muted/30 p-3">
            <ScrollArea className="w-full">
            <ScrollAreaViewport className="w-full">
            <div className="min-w-max pb-2">
              {/* Header row with month labels */}
              <div className="flex">
                <div className="w-[60px] flex-shrink-0" />
                {previewData.cells.map((cell) => (
                  <div
                    key={`header-${cell.monthIndex}`}
                    className="w-[90px] flex-shrink-0 px-2 py-1 text-center"
                  >
                    <span className="text-xs font-medium text-muted-foreground">{cell.label}</span>
                  </div>
                ))}
              </div>
              
              {/* Before row */}
              <div className="flex items-center">
                <div className="w-[60px] flex-shrink-0 text-xs text-muted-foreground pr-2">Before</div>
                {previewData.cells.map((cell) => (
                  <div
                    key={`before-${cell.monthIndex}`}
                    className="w-[90px] flex-shrink-0 px-2"
                  >
                    <div className="px-2 py-1 rounded border bg-background text-center">
                      <span className="text-sm font-medium">{formatValueOnly(cell.before)}</span>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* After row */}
              <div className="flex items-center mt-1">
                <div className="w-[60px] flex-shrink-0 text-xs text-muted-foreground pr-2">After</div>
                {previewData.cells.map((cell) => (
                  <div
                    key={`after-${cell.monthIndex}`}
                    className="w-[90px] flex-shrink-0 px-2"
                  >
                    <div className={`px-2 py-1 rounded border text-center ${cell.before !== cell.after ? 'bg-primary/10 border-primary/30' : 'bg-background'}`}>
                      <span className="text-sm font-medium">{formatValueOnly(cell.after)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            </ScrollAreaViewport>
            <ScrollAreaScrollbar orientation="horizontal" />
            </ScrollArea>
        </div>
        
        {/* Truncation Warning */}
        {willTruncate && truncationInfo && (
          <Alert variant="warning" size="compact">
            <AlertDescription>
              <span className="font-medium">Warning:</span> Budget from {truncationInfo.rangeLabel} ({formatValue(truncationInfo.hours)} across {truncationInfo.monthCount} month{truncationInfo.monthCount > 1 ? 's' : ''}) will not be shifted - destination falls outside project range
            </AlertDescription>
          </Alert>
        )}
      </div>
    </div>
  );

  // Shared footer buttons
  const FooterButtons = () => (
    <>
      <Button variant="outline" onClick={() => onOpenChange(false)} className="flex-1">
        Cancel
      </Button>
      <Button onClick={handleApply} className="flex-1">
        Apply Shift
      </Button>
    </>
  );

  // Render as Dialog (Modal) when expanded
  if (isExpanded) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <div>
                <DialogTitle>Shift {getBudgetLabel()}</DialogTitle>
                <DialogDescription>
                  Move budget {getValueLabel() || 'values'} from one time period to another for {roleName}
                </DialogDescription>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsExpanded(false)}
                title="Return to sheet view"
              >
                <Minimize2 className="h-4 w-4" />
              </Button>
            </div>
          </DialogHeader>

          <div className="py-4">
            <FormContent />
          </div>

          <DialogFooter>
            <FooterButtons />
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  // Render as Sheet (default)
  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="sm:max-w-md flex flex-col p-0">
          <SheetHeader className="px-6 pt-6 pb-4">
            <div>
              <SheetTitle className="text-[20px] font-semibold">Shift {getBudgetLabel()}</SheetTitle>
              <SheetDescription className="text-sm">
                Move budget {getValueLabel() || 'values'} from one time period to another for {roleName}
              </SheetDescription>
            </div>
          </SheetHeader>

          <div className="flex-1 overflow-y-auto px-6 py-4">
            <FormContent />
          </div>

          <SheetFooter className="sticky bottom-0 bg-background border-t px-6 py-4 mt-auto flex-row gap-2">
            <FooterButtons />
          </SheetFooter>
        </SheetContent>
      </Sheet>

      {/* Truncation Confirmation Dialog */}
      <AlertDialog open={showTruncationConfirm} onOpenChange={setShowTruncationConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Partial Shift</AlertDialogTitle>
            <AlertDialogDescription>
              {truncationInfo && (
                <>
                  The budget from {truncationInfo.rangeLabel} ({formatValue(truncationInfo.hours)} across {truncationInfo.monthCount} month{truncationInfo.monthCount > 1 ? 's' : ''}) cannot be shifted as its destination would fall outside the project date range. This data will remain in its original position.
                  <br /><br />
                  Do you want to continue with shifting the remaining months?
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmTruncation}>
              Continue with Shift
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
